/**
 * Continuing review service helpers.
 *
 * All privileged operations go through SECURITY DEFINER RPCs (creation is
 * RPC-only — the browser never inserts CONTINUING_REVIEW rows directly) or the
 * admin-authenticated continuing_review_reminder_email edge function. Each of
 * those re-checks feature flags and authorization server-side; nothing here is
 * a security boundary.
 *
 * Functions take the Supabase client as their first argument (the pattern used
 * by ShareAccessManager) so components pass the context client and tests pass
 * a mock.
 */

import { TypedSupabaseClient } from '../lib/supabaseClient';
import { Database } from '../types/supabase';

export type ContinuingReviewEligibility =
  Database['public']['Functions']['get_continuing_review_eligibility']['Returns'][number];

export type ContinuingReviewQueueRow =
  Database['public']['Functions']['list_continuing_review_queue']['Returns'][number];

export type CreateContinuingReviewResult =
  Database['public']['Functions']['create_continuing_review_submission']['Returns'][number];

export type ContinuingReviewReminderRow =
  Database['public']['Tables']['continuing_review_reminders']['Row'];

/**
 * Eligibility probe for DecisionView: may the current user start (or resume)
 * a continuing review from this source submission?
 */
export const getContinuingReviewEligibility = async (
  supabase: TypedSupabaseClient,
  sourceSubmissionId: string
): Promise<ContinuingReviewEligibility | null> => {
  const { data, error } = await supabase.rpc('get_continuing_review_eligibility', {
    p_source_submission_id: sourceSubmissionId,
  });

  if (error) {
    console.error('Error fetching continuing review eligibility:', error);
    return null;
  }

  return Array.isArray(data) && data.length > 0 ? data[0] : null;
};

/**
 * Start a continuing review (or get back the already-open one). Server-side
 * the RPC enforces ownership, source status/payment, eligibility window, and
 * the continuing_reviews_creation_enabled flag, and is race-safe.
 */
export const initializeContinuingReview = async (
  supabase: TypedSupabaseClient,
  sourceSubmissionId: string
): Promise<CreateContinuingReviewResult> => {
  const { data, error } = await supabase.rpc('create_continuing_review_submission', {
    p_source_submission_id: sourceSubmissionId,
  });

  if (error) {
    console.error('Error creating continuing review submission:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    throw new Error(error.message || 'Unable to start continuing review');
  }

  const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
  if (!row) {
    throw new Error('Unable to start continuing review');
  }

  return row;
};

/** Admin: load the continuing-review queue (flag gated server-side). */
export const listContinuingReviewQueue = async (
  supabase: TypedSupabaseClient,
  windowDays: number = 60,
  includeStates?: string[]
): Promise<ContinuingReviewQueueRow[]> => {
  const { data, error } = await supabase.rpc('list_continuing_review_queue', {
    p_window_days: windowDays,
    ...(includeStates ? { p_include_states: includeStates } : {}),
  });

  if (error) {
    console.error('Error loading continuing review queue:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    throw new Error(error.message || 'Unable to load continuing review queue');
  }

  return data ?? [];
};

/** Admin: reminder history for one source submission (RLS scoped). */
export const listRemindersForSubmission = async (
  supabase: TypedSupabaseClient,
  sourceSubmissionId: string
): Promise<ContinuingReviewReminderRow[]> => {
  const { data, error } = await supabase
    .from('continuing_review_reminders')
    .select('*')
    .eq('source_submission_id', sourceSubmissionId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error loading reminder history:', error);
    return [];
  }

  return data ?? [];
};

export interface SendReminderParams {
  sourceSubmissionId: string;
  recipientEmail?: string;
  ccEmails?: string[];
  customMessage?: string;
  idempotencyToken?: string;
}

export interface SendReminderResult {
  status: string;
  reminder_id: string | null;
  provider_message_id?: string | null;
  error?: string;
}

/** Admin: send a continuing-review reminder via the edge function. */
export const sendContinuingReviewReminder = async (
  supabase: TypedSupabaseClient,
  params: SendReminderParams
): Promise<SendReminderResult> => {
  const { data, error } = await supabase.functions.invoke('continuing_review_reminder_email', {
    body: {
      source_submission_id: params.sourceSubmissionId,
      ...(params.recipientEmail ? { recipient_email: params.recipientEmail } : {}),
      ...(params.ccEmails && params.ccEmails.length > 0 ? { cc_emails: params.ccEmails } : {}),
      ...(params.customMessage ? { custom_message: params.customMessage } : {}),
      ...(params.idempotencyToken ? { idempotency_token: params.idempotencyToken } : {}),
    },
  });

  if (error) {
    console.error('Error sending continuing review reminder:', error);
    throw new Error(error.message || 'Unable to send reminder');
  }

  return data as SendReminderResult;
};

export interface DecisionMetadataParams {
  reviewDecisionId: string;
  continuingReviewRequired: boolean;
  intervalMonths?: number | null;
  notRequiredReason?: string | null;
  windowDays?: number | null;
  basis?: string | null;
  rationale?: string | null;
  regulatoryBasisCodes?: string[];
}

/**
 * Admin: record/remediate the continuing-review determination on a decision.
 * Atomic on the server (decision fields + basis codes change together).
 */
export const updateContinuingReviewDecisionMetadata = async (
  supabase: TypedSupabaseClient,
  params: DecisionMetadataParams
): Promise<boolean> => {
  const { data, error } = await supabase.rpc('update_continuing_review_decision_metadata', {
    p_review_decision_id: params.reviewDecisionId,
    p_continuing_review_required: params.continuingReviewRequired,
    p_interval_months: params.intervalMonths ?? null,
    p_not_required_reason: params.notRequiredReason ?? null,
    p_window_days: params.windowDays ?? null,
    p_basis: params.basis ?? null,
    p_rationale: params.rationale ?? null,
    p_regulatory_basis_codes: params.regulatoryBasisCodes ?? [],
  });

  if (error) {
    console.error('Error updating continuing review decision metadata:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    throw new Error(error.message || 'Unable to update continuing review metadata');
  }

  return data === true;
};

/** Regulatory basis codes for admin selectors. */
export const listRegulatoryBasisCodes = async (supabase: TypedSupabaseClient) => {
  const { data, error } = await supabase
    .from('regulatory_basis_codes')
    .select('*')
    .order('authority')
    .order('code');

  if (error) {
    console.error('Error loading regulatory basis codes:', error);
    return [];
  }

  return data ?? [];
};
